import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { appConfirmMessage } from '../../utils/appConfirm';
import { FaSearch } from 'react-icons/fa';
import { HiPencil } from 'react-icons/hi';
import { RiDeleteBinLine } from 'react-icons/ri';
import { LuCircleAlert } from 'react-icons/lu';
import { graphqlRequest } from '../../utils/graphqlRequest';
import IncomeForm from './IncomeForm';
import './styles/Income.css';

const INCOME_QUERY = `
  query GetFinanceIncome {
    getFinanceIncome {
      totalIncome
      totalPaid
      totalPending
      records {
        id
        date
        client
        project
        source
        amount
        status
        invoiceNumber
      }
    }
  }
`;

const DELETE_INCOME_MUTATION = `
  mutation DeleteFinanceIncome($id: String!) {
    deleteFinanceIncome(id: $id)
  }
`;

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function Income() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const fetchIncome = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await graphqlRequest(INCOME_QUERY);
      setData(result.getFinanceIncome);
    } catch (err) {
      console.error('Failed to fetch income data:', err);
      setError('Failed to load income data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncome();
  }, []);

  const handleDelete = async (id) => {
    if (!await appConfirmMessage('Are you sure you want to delete this income record?')) return;
    try {
      await graphqlRequest(DELETE_INCOME_MUTATION, { id });
      fetchIncome();
    } catch (err) {
      console.error('Failed to delete income:', err);
      toast.error('Failed to delete record. It may be an invoice record which cannot be deleted from here.');
    }
  };

  if (loading) {
    return (
      <div className="income-page">
        <div className="income-loading">
          <div className="spinner" />
          <p>Loading income data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="income-page">
        <div className="income-error">
          <LuCircleAlert size={40} />
          <p>{error}</p>
          <button onClick={fetchIncome}>Retry</button>
        </div>
      </div>
    );
  }

  const records = data?.records || [];
  const filtered = records.filter((row) => {
    const matchText = Object.values(row)
      .join(' ')
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || row.status === statusFilter;
    return matchText && matchStatus;
  });

  return (
    <div className="income-page">
      {/* ADD INCOME FORM MODAL */}
      <IncomeForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={fetchIncome}
      />

      {/* HEADER */}
      <div className="income-title">
        <h1>Income</h1>
        <p>Track and manage all incoming payments</p>
      </div>

      {/* SUMMARY */}
      <div className="income-summary">
        <div className="summary-box">
          <p>Total Income</p>
          <h2>{formatCurrency(data?.totalIncome)}</h2>
        </div>

        <div className="summary-box paid">
          <p>Paid</p>
          <h2>{formatCurrency(data?.totalPaid)}</h2>
        </div>

        <div className="summary-box pending">
          <p>Pending</p>
          <h2>{formatCurrency(data?.totalPending)}</h2>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="income-toolbar">
        <div className="income-search">
          <FaSearch />
          <input
            placeholder="Search by client, project, or source..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option>All</option>
          <option>Paid</option>
          <option>Pending</option>
          <option>Partial</option>
        </select>

        <button className="filter-btn">More Filters</button>
        <button className="add-btn" onClick={() => setFormOpen(true)}>
          + Add Income
        </button>
      </div>

      {/* TABLE */}
      <div className="income-table-card">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Client</th>
              <th>Project</th>
              <th>Source</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  No income records found
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id}>
                  <td>{row.date}</td>
                  <td>{row.client}</td>
                  <td>{row.project}</td>
                  <td>{row.source}</td>
                  <td>{formatCurrency(row.amount)}</td>
                  <td>
                    <span className={`status ${row.status.toLowerCase()}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="actions">
                    <HiPencil title="Edit" />
                    <RiDeleteBinLine
                      title="Delete"
                      onClick={() => handleDelete(row.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
